import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { buildExampleSegments } from "@/lib/vocabulary-highlight";
import { sanitizeExample } from "@/lib/vocabulary-text";
import { useDeckWords } from "@/hooks/use-deck-words";

/**
 * The subject word (the entry the example belongs to) — the one blue accent
 * the design system reserves for actions and focus.
 */
const subjectHighlightClass =
  "rounded-[3px] bg-primary/15 px-0.5 font-medium text-primary dark:bg-primary/25 dark:text-primary/90";

/**
 * Another word from the learner's deck — the amber end of the ramp, so a
 * related word never reads as the subject.
 */
const relatedHighlightClass =
  "rounded-[3px] bg-amber-500/20 px-0.5 font-medium text-amber-800 dark:bg-amber-500/25 dark:text-amber-200";

interface HighlightedExampleProps {
  /** The raw example sentence. Generation artifacts are stripped on render. */
  text?: string | null;
  /** The entry's own word — highlighted in the primary accent. */
  word?: string | null;
  /**
   * Deck words to highlight. Defaults to the learner's saved deck; pass an
   * explicit list when the caller already has it.
   */
  words?: string[];
  /** Rendered as a block by default; use "span" inside existing paragraphs. */
  as?: "p" | "span";
  /** Wrap the sentence in typographic quotes. */
  quote?: boolean;
  className?: string;
}

const ExampleBody = ({
  text,
  word,
  words,
  as: Tag = "p",
  quote = false,
  className,
}: Omit<HighlightedExampleProps, "words"> & { words: string[] }) => {
  const example = useMemo(() => sanitizeExample(text), [text]);

  const segments = useMemo(
    () => buildExampleSegments(example, words, word),
    [example, words, word],
  );

  if (!example) return null;

  return (
    <Tag className={className}>
      {quote ? "“" : null}
      {segments.map((segment, index) =>
        segment.word ? (
          <mark
            key={index}
            data-deck-word={segment.word}
            title={segment.isSubject ? segment.word : `In your deck: ${segment.word}`}
            className={cn(
              segment.isSubject ? subjectHighlightClass : relatedHighlightClass,
            )}
          >
            {segment.text}
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
      {quote ? "”" : null}
    </Tag>
  );
};

/** Renders with the learner's saved deck as the highlight source. */
const DeckExample = (props: Omit<HighlightedExampleProps, "words">) => {
  const deckWords = useDeckWords();
  return <ExampleBody {...props} words={deckWords} />;
};

/**
 * An example sentence with the learner's own vocabulary highlighted.
 *
 * Every surface that shows a vocabulary example should render it through
 * this component so the same words light up everywhere — the deck card, the
 * word sheet, practice, collections. One component, one behaviour, no drift.
 */
export const HighlightedExample = (props: HighlightedExampleProps) =>
  props.words ? <ExampleBody {...props} words={props.words} /> : <DeckExample {...props} />;
